import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Quote, Sparkles } from 'lucide-react';
import { FadeUp } from '../../../components/shared/Motion';
import { EditableText, EditableImage, HrefPill } from '../../components';
import { getIcon } from '../iconMap';

function LeadershipTabs({ data, onChange }) {
  const items = data.items || [];
  const [leadIdx, setLeadIdx] = useState(0);
  const update = (patch) => onChange && onChange({ ...data, ...patch });
  const updateItem = (i, patch) => {
    const next = items.map((x, idx) => (idx === i ? { ...x, ...patch } : x));
    update({ items: next });
  };
  const ctas = data.ctas || [];
  const updateCta = (i, patch) => {
    const next = ctas.map((c, idx) => (idx === i ? { ...c, ...patch } : c));
    update({ ctas: next });
  };
  const cur = items[leadIdx] || items[0];
  if (!cur) return null;

  return (
    <section id="leadership" className="relative py-24 lg:py-32 bg-olive-light overflow-hidden">
      <div className="container-page">
        <FadeUp className="text-center max-w-3xl mx-auto mb-10">
          <span className="eyebrow justify-center"><span className="h-px w-8 bg-olive" />
            <EditableText value={data.eyebrow} onChange={(t) => update({ eyebrow: t })} as="span" />
          </span>
          <h2 className="section-title mt-4">
            <EditableText value={data.heading_prefix} onChange={(t) => update({ heading_prefix: t })} as="span" />
            <span className="text-gradient"><EditableText value={data.heading_highlight} onChange={(t) => update({ heading_highlight: t })} as="span" /></span>
            <EditableText value={data.heading_suffix} onChange={(t) => update({ heading_suffix: t })} as="span" />
          </h2>
        </FadeUp>

        <FadeUp delay={0.05}>
          <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
            {items.map((p, i) => {
              const active = leadIdx === i;
              const PI = getIcon(p.icon);
              return (
                <button
                  key={p.key || i}
                  type="button"
                  onClick={() => setLeadIdx(i)}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[12px] font-bold uppercase tracking-[0.18em] transition-all duration-300 ${
                    active
                      ? 'bg-accent text-ink shadow-[0_14px_30px_-10px_rgba(197,120,99,0.6)]'
                      : 'bg-white text-olive-deep border border-olive/15 hover:border-accent/60 hover:text-olive'
                  }`}
                >
                  <PI size={13} />
                  <EditableText value={p.role} onChange={(t) => updateItem(i, { role: t })} as="span" />
                </button>
              );
            })}
          </div>
        </FadeUp>

        <FadeUp delay={0.1}>
          <div className="relative rounded-[28px] p-8 lg:p-12 bg-white" style={{ border: '2px dashed rgba(161,201,164,0.32)' }}>
            <Quote size={84} className="absolute -top-4 right-6 lg:right-12 text-accent opacity-90" strokeWidth={1.4} />
            <AnimatePresence mode="wait">
              <motion.div
                key={cur.key}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="grid lg:grid-cols-12 gap-10 items-center"
              >
                <div className="lg:col-span-4">
                  <div className="relative">
                    <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_50px_-15px_rgba(46,58,24,0.4)]">
                      <EditableImage
                        src={cur.image?.src}
                        alt={cur.image?.alt || cur.role}
                        onChange={(src, alt) => updateItem(leadIdx, { image: { src, alt } })}
                        className="h-[420px] w-full object-cover"
                      />
                    </div>
                    <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent text-ink font-bold text-xs uppercase tracking-[0.18em] shadow-[0_14px_30px_-10px_rgba(197,120,99,0.75)] whitespace-nowrap">
                      <Sparkles size={12} />
                      <EditableText value={cur.name} onChange={(t) => updateItem(leadIdx, { name: t })} as="span" />
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-8">
                  <span className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-olive">
                    <span className="h-px w-8 bg-olive" />
                    <EditableText value={cur.role} onChange={(t) => updateItem(leadIdx, { role: t })} as="span" />
                    's Message
                  </span>
                  <EditableText value={cur.heading} onChange={(t) => updateItem(leadIdx, { heading: t })} as="h3" className="mt-4 font-display text-3xl lg:text-4xl text-olive-deep leading-tight" />
                  <EditableText value={cur.body} onChange={(t) => updateItem(leadIdx, { body: t })} as="p" multiline className="mt-5 text-ink/75 leading-relaxed text-lg" />
                  {cur.body_extra && (
                    <EditableText value={cur.body_extra} onChange={(t) => updateItem(leadIdx, { body_extra: t })} as="p" multiline className="mt-4 text-ink/70 leading-relaxed" />
                  )}
                  <div className="mt-6 text-sm font-semibold text-olive">— <EditableText value={cur.name} onChange={(t) => updateItem(leadIdx, { name: t })} as="span" /></div>
                  <div className="mt-6 flex flex-wrap gap-3">
                    {ctas.map((c, i) => {
                      const isPrimary = (c.variant || 'primary') === 'primary';
                      return (
                        <div key={i} className="inline-flex flex-col">
                          <Link
                            to={c.href || '#'}
                            className={
                              isPrimary
                                ? 'inline-flex items-center gap-2 px-6 py-3 rounded-full bg-olive text-white font-semibold text-sm hover:bg-olive-deep hover:-translate-y-0.5 hover:shadow-glow-olive transition-all duration-300'
                                : 'inline-flex items-center gap-2 px-6 py-3 rounded-full border border-olive/30 text-olive-deep font-semibold text-sm hover:bg-olive-light hover:border-olive/60 transition-all duration-300'
                            }
                          >
                            <EditableText value={c.label} onChange={(t) => updateCta(i, { label: t })} as="span" />
                            {isPrimary && <ArrowRight size={16} />}
                          </Link>
                          <HrefPill value={c.href} onChange={(h) => updateCta(i, { href: h })} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

export default LeadershipTabs;
